import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '@SonarCX/store'
import { FlowType, createDefaults, formatData } from './helpers'
import {
  getFlows,
  postFlow,
  putFlow,
  deleteFlow as deleteFlowAPI
} from '@SonarCX/lib/api'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'

type FlowState = {
  flows: Record<FlowType, APIFlow[]>
  loaded: Record<FlowType, boolean>
  loading: Record<FlowType, boolean>
}

const initialState: FlowState = {
  flows: createDefaults([]),
  loaded: createDefaults(false),
  loading: createDefaults(false)
}

const flowsSlice = createSlice({
  name: 'flows',
  initialState,
  reducers: {
    startLoad: (state: FlowState, action: PayloadAction<FlowType>) => {
      state.loading[action.payload] = true
    },

    finishLoad: (
      state: FlowState,
      action: PayloadAction<{ type: FlowType; data: APIFlow[] }>
    ) => {
      const { type, data } = action.payload
      state.flows[type] = data.map(formatData)
      state.loaded[type] = true
      state.loading[type] = false
    },

    updatePartial: (state: FlowState, action: PayloadAction<APIFlow[]>) => {
      action.payload.forEach(item => {
        const flow = formatData(item)
        const type = flow.object_type

        // Add or replace based on payload ids
        const idx = state.flows[type].findIndex(other => other.id === flow.id)
        if (idx >= 0) {
          state.flows[type][idx] = flow
        } else {
          state.flows[type].push(flow)
        }
      })
    },

    deleteFlow: (state: FlowState, action: PayloadAction<string>) => {
      Object.entries(state.flows).forEach(([type, flows]) => {
        state.flows[type] = flows.filter(flow => flow.id !== action.payload)
      })
    }
  }
})

const {
  startLoad,
  finishLoad,
  updatePartial,
  deleteFlow: removeFlow
} = flowsSlice.actions

export const fetchFlow = (type: FlowType) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(startLoad(type))
      const flows = await getFlows(type)
      dispatch(finishLoad({ type, data: flows }))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const createFlow = (item: FlowUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      const flow = await postFlow(item)
      if (flow) {
        dispatch(updatePartial([flow]))
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const updateFlow = (id: string, item: FlowUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      const flow = await putFlow(id, item)
      if (flow) {
        dispatch(updatePartial([flow]))
        dispatch(AddSuccessAlert('Changes saved', 500))
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const deleteFlow = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await deleteFlowAPI(id)
      dispatch(removeFlow(id))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectFlows = (state: RootState) => state.flows

export default flowsSlice.reducer
